/**
 * The Billing page's overview blocks: what this cycle has accrued and
 * where the cycle stands (CycleSummary), and the quieter facts of the
 * account: plan, payment method, billing email (AccountFacts). Each sits
 * in its own SettingsSection, so emphasis comes from placement and
 * typeface contrast, not from size or color (design-principles §1).
 */

import type { BillingAccrual, BillingOverviewResponse } from '@/api/generated/model'
import {
  cycleProgress,
  formatAmount,
  formatDay,
  formatPeriod,
  paymentMethodLabel,
} from '@/components/billing/billing'
import { EYEBROW } from '@/components/ui/eyebrow'
import { countLabel } from '@/lib/utils'

function CycleProgress({ day, days }: { day: number; days: number }): React.JSX.Element {
  const pct = Math.round((day / days) * 100)
  return (
    <div className="space-y-1.5">
      <div
        className="h-px w-full bg-border"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={days}
        aria-valuenow={day}
        aria-label={`Day ${String(day)} of ${String(days)} in this billing cycle`}
      >
        <div className="h-px bg-foreground" style={{ width: `${String(pct)}%` }} />
      </div>
      <p className="font-mono text-xs text-muted-foreground">
        Day {day} of {days}
      </p>
    </div>
  )
}

export function CycleSummary({ accrual }: { accrual: BillingAccrual }): React.JSX.Element {
  const progress = cycleProgress(accrual.period_start, accrual.period_end, new Date())
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="space-y-1">
          <p className={EYEBROW}>Accrued</p>
          <p className="font-mono text-xl tabular-nums text-foreground">
            {formatAmount(accrual.amount_due, accrual.currency)}
          </p>
          <p className="text-sm text-muted-foreground">
            {formatPeriod(accrual.period_start, accrual.period_end)}
          </p>
        </div>
        <div className="space-y-1 text-right">
          <p className={EYEBROW}>Next invoice</p>
          <p className="font-mono text-sm text-foreground">{formatDay(accrual.period_end)}</p>
        </div>
      </div>
      <CycleProgress day={progress.day} days={progress.days} />
    </div>
  )
}

interface AccountFact {
  label: string
  value: string
  detail: string | null
}

function accountFacts(overview: BillingOverviewResponse): AccountFact[] {
  const { customer } = overview
  const plan =
    overview.mode === 'gpu' && overview.dedicated_gpus !== null
      ? `${countLabel(overview.dedicated_gpus, 'dedicated GPU')} on the monthly base`
      : null
  return [
    ...(plan !== null ? [{ label: 'Plan', value: plan, detail: null }] : []),
    {
      label: 'Payment method',
      value: customer ? paymentMethodLabel(customer.payment_method) : '—',
      detail: null,
    },
    {
      label: 'Billing email',
      value: customer?.email ?? '—',
      detail: customer?.name ?? null,
    },
    ...(overview.floor_date !== null
      ? [{ label: 'Billed since', value: formatDay(`${overview.floor_date}T00:00:00Z`), detail: null }]
      : []),
  ]
}

export function AccountFacts({ overview }: { overview: BillingOverviewResponse }): React.JSX.Element {
  return (
    <dl className="grid gap-6 sm:grid-cols-3">
      {accountFacts(overview).map((fact) => (
        <div key={fact.label} className="space-y-0.5">
          <dt className={EYEBROW}>{fact.label}</dt>
          <dd className="break-words text-sm text-foreground">{fact.value}</dd>
          {fact.detail !== null && (
            <dd className="break-words text-xs text-muted-foreground">{fact.detail}</dd>
          )}
        </div>
      ))}
    </dl>
  )
}
